import {
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import MDSnackbar from 'components/MDSnackbar';
import MDTypography from 'components/MDTypography';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import * as Func from 'services/Function';
import { FetchData } from 'services/Request';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import PosPrint from './PosPrint';

function PosBillRecovery({ lastbill }) {
  const mod = 'pointofsale';
  const saleOrderId = useRef(null);
  const [modalChange, setModalChange] = useState(false);
  const [summary, setSummary] = useState({});
  const initStatus = useRef({});
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [notify, setNotify] = useState(null);
  const printRef = useRef();
  const isInit = useRef(true);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const modal = (isCash) => {
    Swal.fire({
      title: 'พบรายการบิลที่ค้างอยู่',
      icon: 'warning',
      showDenyButton: isCash,
      showCancelButton: true,
      confirmButtonText: 'พิมพ์ใบเสร็จ',
      confirmButtonColor: '#3085d6',
      denyButtonText: 'เงินทอน',
      denyButtonColor: '#3085d6',
      cancelButtonText: 'ปิด',
      cancelButtonColor: '#dc3545',
      allowOutsideClick: false,
      allowEscapeKey: false,
      preConfirm: () => {
        printRef.current.print(saleOrderId.current);
        return false;
      },
      preDeny: () => {
        setModalChange(true);
        return true;
      },
      didClose: () => {
        if (
          initStatus.current.paymentType === 'TRANSFER' ||
          initStatus.current.paymentType === 'CREDIT'
        ) {
          localStorage.setItem(
            'pos_status',
            JSON.stringify({ ...initStatus.current, step: 'print', status: 'success' })
          );
        } else if (initStatus.current.paymentType === 'CASH') {
          localStorage.setItem(
            'pos_status',
            JSON.stringify({
              ...initStatus.current,
              step: 'change',
              status: 'success',
              paymentType: 'CASH',
            })
          );
        }
      },
    });
  };

  const setSaleOrderId = (so_code) => {
    FetchData('OPTIONS', `/${mod}/getSaleOrderId`, app.token, {
      lang: app.lang,
      so_code: so_code,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          if (result.summary) setSummary(result.summary);
          saleOrderId.current = result.id;
        } else {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  // INIT
  useEffect(() => {
    if (!isInit.current) {
      return;
    }
    isInit.current = false;

    const status = JSON.parse(localStorage.getItem('pos_status'));
    if (
      !status ||
      ((status.paymentType === 'TRANSFER' || status.paymentType === 'CREDIT') &&
        status.step === 'print' &&
        status.status === 'success') ||
      (status.paymentType === 'CASH' && status.step === 'change' && status.status === 'success')
    ) {
      return;
    }

    initStatus.current = status;

    if (status.soId) {
      saleOrderId.current = status.soId;
    } else {
      setSaleOrderId(lastbill);
    }

    if (status.paymentType === 'CASH' && status.summaryChange) {
      setSummary(status.summaryChange);
    }

    modal(status.paymentType === 'CASH');
  }, [lastbill]);

  const clear = () => {
    setModalChange(false);
    modal(true);
  };

  const renderChange = () => {
    return (
      <Dialog
        open={modalChange}
        maxWidth='xs'
        keepMounted
        aria-describedby='dialog-change'
        fullWidth
        onClose={clear}
      >
        <DialogTitle>
          <i className='fad fa-money-bill-alt color-danger' /> &nbsp; เงินทอน
          <IconButton
            aria-label='close'
            onClick={clear}
            autoFocus
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          <TableContainer sx={{ boxShadow: 0 }}>
            <Table aria-label='summary table'>
              <TableBody>
                <TableRow>
                  <TableCell align='right' width={200}>
                    <MDTypography variant='h2'>รวมทั้งสิ้น :</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h1' color='info'>
                      {Func.numberDisplay(summary.nettotal)}
                    </MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right'>
                    <MDTypography variant='h2'>รับเงิน :</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h1' color='success'>
                      {Func.numberDisplay(summary.money_receive)}
                    </MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right'>
                    <MDTypography variant='h2'>เงินทอน :</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h1' color='error'>
                      {Func.numberDisplay(summary.money_change)}
                    </MDTypography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      {renderChange()}
      {notify}
      <PosPrint ref={printRef} paymentType={initStatus.current.paymentType} />
    </>
  );
}

PosBillRecovery.propTypes = {
  lastbill: PropTypes.string.isRequired,
};

export default PosBillRecovery;
